import CurrencyFormatter from 'currency-formatter'
import lodash from 'lodash'

import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import numberUtils from '@/utils/numberUtils'
import store from '@/store'
import userHelper from '@/utils/userHelper'

import { contactTypes } from '@/api/users/consts'
import { emailActionTexts } from '@/modules/tenantProjects/submissions/consts'
import { emailDateFormat } from '@/utils/regex'
import { EmailTypes, tenantProjectTabs, tenantProjectRouteNames, tenantTabsViewArr } from '@/utils/consts'
import { getBatchDocName, getTabIndexByName } from '@/modules/tenantProjects/utils'
import { getEnvParameter } from '@/api/settings/request'
import { getLink, getLinkToTab, buildEmail, getPublicLink } from './util'
import { getSameBuildingUsersByPermission } from '@/api/users/request'
import { PermTypeMapKeys } from '@/utils/aclHelper'
import { SEND_EMAIL } from './consts'
import { tenantProjectTypes, companyType } from '@/utils/locale'
import { TENANT_PROJECT_TYPES } from '@/modules/dashboards/consts'
import { sortTechComments } from '@/utils/tenantProjectGuidelineUtils'
import { getLatestNotifyAccountingAmount } from '@/modules/tenantProjects/projectControl/deposit/sync.service'

function getProjectInfo () {
  const project = store.getters['tenantProject/currentTenantProject']
  const building = lodash.get(project.tenancy, 'building.fullName', '')
  const title = lodash.get(project, 'projectName')
  const projectInfo = {
    id: project.id,
    originType: project.type,
    type: i18n.t(`tenancyProject.${lodash.toLower(project.type)}`),
    projectName: project.projectName,
    building,
    projectLocation: project.projectLocation.join(', '),
    company: lodash.get(project.tenancy, 'company.name'),
    tradeName: lodash.get(project.tenancy, 'tradeName'),
    tenancyId: lodash.get(project.tenancy, 'id'),
    title,
    premises: (project.floors || []).join(', '),
  }
  const engineerEntry = store.getters['settings/engineerEntry']
  const vendorEntry = store.getters['settings/vendorEntry']
  const bWorkPermitsEnabled = store.getters['regions/enableWorkPermitsFeature']
  const tenantTabs = lodash.clone(tenantTabsViewArr)
  if (bWorkPermitsEnabled) {
    tenantTabs.splice(2, 0, tenantProjectTabs.contractorInfo, tenantProjectTabs.workPermit)
  }
  const projectLink = getLink(tenantProjectRouteNames.tenantProject, project.id, engineerEntry)
  const tabIndex = tenantTabs.length - 1
  const projectLinkForTenant = getLink(tenantProjectRouteNames.tenantProjectDetail, project.id, vendorEntry, tabIndex)
  return [projectInfo, projectLink, projectLinkForTenant, tabIndex]
}

function getInvitedTenancyAccountUsername (accounts) {
  return accounts.reduce((result, account) => {
    const item = account.TenancyAccount
    if (!!lodash.get(item, 'invitedAt') &&
      (lodash.get(item, 'isAdmin', false) || !!lodash.get(item, 'perms.TenantProject'))) {
      result.push(account.username)
    }
    return result
  }, [])
}

export function getRepresentativeEmails () {
  const project = store.getters['tenantProject/currentTenantProject']
  const accountsOfTenantProject = store.getters['tenantProject/accounts']
  const emailsOfTenantProject = accountsOfTenantProject.reduce((result, item) => {
    if (lodash.get(item, 'TenantProjectAccount.invitedAt', false)) {
      result.push(item.username)
    }
    return result
  }, [])

  const allAccountsOfTenancy = lodash.get(project, 'tenancy.accounts', [])
  const emailsOfTenancy = getInvitedTenancyAccountUsername(allAccountsOfTenancy)

  return [...emailsOfTenantProject, ...emailsOfTenancy].filter(item => !!item)
}

export function getMainContractorEmail () {
  const project = store.getters['tenantProject/currentTenantProject']
  const mainContractor = lodash.get(project, 'mainContractor.primaryContact') || {}
  return mainContractor.invitedAt ? mainContractor.email : ''
}

function getUserInfo (user) {
  if (!user) { return {} }
  const contact = (user.contacts || []).filter(contact => contact.type === contactTypes.EMAIL)[0]
  const email = contact && contact.value ? contact.value : user.username
  return {
    ...user,
    email,
    name: userHelper.getUserName(user.username, user.displayName),
  }
}

async function getUserEmailsOfSameBuilding (permType, { needTrRep = false } = {}) {
  const users = await getUsersOfSameBuilding(permType, { needTrRep })
  return users.map(user => user.email || user.username)
}

async function getUsersOfSameBuilding (permType, { needTrRep = false } = {}) {
  const currentTenantProject = store.getters['tenantProject/currentTenantProject']
  const tenancy = currentTenantProject.tenancy
  const users = await getSameBuildingUsersByPermission({
    buildingId: tenancy.building.id,
    tenancyId: needTrRep ? tenancy.id : undefined,
    tenancyType: tenancy.type,
    includeSelf: true,
    permTypes: permType,
  })
  return users
}

function getMetaData (extMetaData) {
  const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
  projectInfo.overViewInfo = projectInfo.originType === tenantProjectTypes.Fitout ? i18n.t('tenantProjects.submission.alterationRenoWorks') : i18n.t('tenantProjects.submission.reinstatementWorks')
  const metaData = {
    ...extMetaData,
    projectInfo,
    projectLink,
    projectLinkForTenant,
  }
  return metaData
}

function getSubmissionMetaData (extMetaData) {
  const metaData = getMetaData(extMetaData)

  const project = store.getters['tenantProject/currentTenantProject']
  metaData.submission = lodash.cloneDeep(extMetaData.action === 'RESEND'
    ? project.submissions[extMetaData.submissionIndex]
    : store.getters['tenantProject/currentTenantSubmission'])
  const completedDate = metaData.submission.completedDate
    ? dateUtil.convertDateToArray(metaData.submission.completedDate, emailDateFormat, ' ')
    : null
  metaData.submission.completedDate = completedDate
  metaData.submission.batchNo = parseInt(metaData.submission.order) + 1
  metaData.submission.designManager = getUserInfo(metaData.submission.designManager)
  metaData.submission.techManager = getUserInfo(metaData.submission.techManager)
  const comments = metaData.submission.techComment.comments || []
  metaData.submission.techComment.comments = sortTechComments(comments).map((section, secIndex) => {
    section.prefix = `${numberUtils.convertToAlphabet(secIndex + 1)}.`
    section.sectionContent.forEach((line, index) => {
      line.prefix = `${index + 1}.`
    })
    return section
  })
  const submittedFiles = project.projectDocs.filter(doc => doc.batchNum !== null && Number(doc.batchNum) === Number(metaData.submission.order))
  metaData.submission.submittedFiles = submittedFiles.map(doc => getBatchDocName(doc)) || []
  return metaData
}

function getPostFitoutSubmissionMetaData (extMetaData) {
  const metaData = getMetaData(extMetaData)

  const project = store.getters['tenantProject/currentTenantProject']
  const comments = metaData.submission.comments.guidelines || []
  metaData.submission.comments.guidelines = sortTechComments(comments).map((section, secIndex) => {
    section.prefix = `${numberUtils.convertToAlphabet(secIndex + 1)}.`
    section.sectionContent.forEach((line, index) => {
      line.prefix = `${index + 1}.`
    })
    return section
  })
  const completedDate = metaData.submission.completedDate
    ? dateUtil.convertDateToArray(metaData.submission.completedDate, emailDateFormat, ' ')
    : null
  metaData.submission.completedDate = completedDate
  const submittedFiles = project.projectDocs.filter(doc => doc.batchNum !== null && Number(doc.batchNum) === Number(metaData.submission.order))
  metaData.submission.submittedFiles = submittedFiles.map(doc => getBatchDocName(doc)) || []
  metaData.submission.orderNo = metaData.submission.order ? Number(metaData.submission.order) + 1 : undefined
  return metaData
}

function buildNotifyMetaData (extMetaData) {
  const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
  const tabIndex = getTabIndexByName('projectControl')
  const projectLinkToProjectCtrl = getLinkToTab(tenantProjectRouteNames.tenantProject, { id: projectInfo.id }, { tabIndex })
  const currentRegion = store.getters['regions/currentRegion'] || {}
  const currency = currentRegion.currency

  const metaData = {
    key: projectInfo.id,
    projectInfo,
    projectLink,
    projectLinkForTenant,
    projectLinkToProjectCtrl,
    currency,
  }
  return metaData
}

async function buildNotifyPMMeta (extMetaData) {
  let commonMetaData = buildNotifyMetaData(extMetaData)
  const canNotifyAccoutingUsers = await getUsersOfSameBuilding(PermTypeMapKeys.TENANT_PROJECT_NOTIFY_ACCOUNTING, { needTrRep: true })
  commonMetaData = { ...commonMetaData, canNotifyAccoutingUsers }

  const sendTo = new Set(canNotifyAccoutingUsers.map(user => user.email || user.username))
  return [commonMetaData, sendTo]
}

export function buildFinalDocsBasicData () {
  const [projectInfo] = getProjectInfo()
  const tabName = 'projectControl'
  const projectLinkToProjContr = getLinkToTab(tenantProjectRouteNames.tenantProjectDetail, { id: projectInfo.id }, { tabName })
  const basicMetaData = {
    projectInfo,
    projectLinkToProjContr,
  }
  return basicMetaData
}

export async function buildReminderData () {
  const mainContractorEmail = getMainContractorEmail()
  const representativeEmails = getRepresentativeEmails()
  const toEmails = new Set([...representativeEmails, mainContractorEmail])
  const basicMetaData = buildFinalDocsBasicData()
  return { basicMetaData, toEmails }
}

async function buildFinalDocsMeta () {
  const ccEmailsEmails = await getUserEmailsOfSameBuilding(PermTypeMapKeys.TENANT_PROJECT_FINAL_DOCS_COMP_EXEP)
  const cc = new Set(ccEmailsEmails)
  const basicMetaData = buildFinalDocsBasicData()
  const metaData = {
    key: basicMetaData.projectInfo.id,
    ...basicMetaData,
    cc,
  }
  const toEmails = new Set(getRepresentativeEmails())
  return { metaData, toEmails }
}

const tenantProjectsNotification = {
  submitProject: async (extMetaData) => {
    const emails = []
    const template = EmailTypes.tenantProjects.submitProject
    const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
    const bccEmails = await getUserEmailsOfSameBuilding(projectInfo.originType === TENANT_PROJECT_TYPES.Fitout ? PermTypeMapKeys.TENANT_PROJECT_RELATED_UPDATE : PermTypeMapKeys.TENANT_PROJECT_RELATED_UPDATE_NO_DESIGN)
    const bcc = new Set(bccEmails)
    const metaData = {
      projectInfo,
      projectLink,
      projectLinkForTenant,
      bcc,
    }
    const toEmails = new Set(getRepresentativeEmails())

    buildEmail(emails, Array.from(toEmails), template, metaData, SEND_EMAIL)
    return emails
  },
  submitDesignDocs: async (extMetaData) => {
    const emails = []
    const template = EmailTypes.tenantProjects.submitDesignDocs
    const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
    const projectLinkToSubmission = getLinkToTab(tenantProjectRouteNames.tenantProject, { id: projectInfo.id }, { tabIndex: tenantProjectTabs.submissions }, process.env.ENGINEER_HOST_NAME)
    const metaData = {
      projectInfo,
      projectLink,
      projectLinkForTenant,
      projectLinkToSubmission,
    }
    const to = await getUserEmailsOfSameBuilding(PermTypeMapKeys.TENANT_PROJECT_DESIGN)
    const toEmails = new Set(to)
    let sendTo = ''
    toEmails.forEach(email => { sendTo = `${email};${sendTo}` })
    buildEmail(emails, sendTo, template, metaData, SEND_EMAIL)
    return emails
  },
  batchStatusChanged: async (extMetaData) => {
    const emails = []
    const template = EmailTypes.tenantProjects.batchStatusChanged
    const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
    const submission = lodash.cloneDeep(store.getters['tenantProject/currentTenantSubmission'])
    const batchOrder = submission.order + 1
    const designComment = emailActionTexts()[submission.designComment.comment]
    const designManager = getUserInfo(submission.designManager)

    const projectLinkToSubmission = getLinkToTab(tenantProjectRouteNames.tenantProject, { id: projectInfo.id }, { tabIndex: tenantProjectTabs.submissions }, process.env.ENGINEER_HOST_NAME)

    const metaData = {
      projectInfo,
      projectLink,
      projectLinkForTenant,
      projectLinkToSubmission,
      submission: { id: submission.id, designComment, designManager, batchOrder },
    }
    let to = await getUserEmailsOfSameBuilding(PermTypeMapKeys.TENANT_PROJECT_TECH_PREV_SEND)
    if (extMetaData.isAutoChangeStatus) {
      const toCP = await getUserEmailsOfSameBuilding(PermTypeMapKeys.TENANT_PROJECT_DESIGN)
      to.push(...toCP)
      metaData.projectInfo.isAutoChangeStatus = true
    }
    const toEmails = new Set(to)
    let sendTo = ''
    toEmails.forEach(email => { sendTo = `${email};${sendTo}` })
    buildEmail(emails, sendTo, template, metaData, SEND_EMAIL)
    return emails
  },
  reassign: () => {
    const emails = []
    const template = EmailTypes.tenantProjects.reassign
    const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
    const submission = lodash.cloneDeep(store.getters['tenantProject/currentTenantSubmission'])
    const designManager = getUserInfo(submission.designManager)
    const projectLinkToSubmission = getLinkToTab(tenantProjectRouteNames.tenantProject, { id: projectInfo.id }, { tabIndex: tenantProjectTabs.submissions }, process.env.ENGINEER_HOST_NAME)
    const metaData = {
      projectInfo,
      projectLink,
      projectLinkForTenant,
      submission: { id: submission.id, designManager },
      projectLinkToSubmission,
    }
    buildEmail(emails, designManager.email, template, metaData, SEND_EMAIL)
    return emails
  },
  nextReviewer: () => {
    const emails = []
    const template = EmailTypes.tenantProjects.nextReviewer
    const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
    const submission = lodash.cloneDeep(store.getters['tenantProject/currentTenantSubmission'])
    const techManager = getUserInfo(submission.techManager)
    const projectLinkToSubmission = getLinkToTab(tenantProjectRouteNames.tenantProject, { id: projectInfo.id }, { tabIndex: tenantProjectTabs.submissions }, process.env.ENGINEER_HOST_NAME)
    const metaData = {
      projectInfo,
      projectLink,
      projectLinkForTenant,
      submission: { id: submission.id, techManager },
      projectLinkToSubmission,
    }
    buildEmail(emails, techManager.email, template, metaData, SEND_EMAIL)
    return emails
  },
  completeSubmission: (extMetaData) => {
    const emails = []
    const template = EmailTypes.tenantProjects.completeSubmission
    const metaData = getSubmissionMetaData(extMetaData)
    buildEmail(emails, extMetaData.to, template, metaData, SEND_EMAIL)
    return emails
  },
  completePostfitout: (extMetaData) => {
    const emails = []
    const template = EmailTypes.tenantProjects.completePostfitout
    const metaData = getPostFitoutSubmissionMetaData(extMetaData)
    buildEmail(emails, extMetaData.to, template, metaData, SEND_EMAIL)
    return emails
  },
  completeGreenFitOut: async (extMetaData, template) => {
    const emails = []
    const metaData = getMetaData(extMetaData)
    metaData.projectInfo.submissionId = metaData.submission.id
    buildEmail(emails, extMetaData.to, template, metaData, SEND_EMAIL)
    return emails
  },
  inspectionFollowUp: async (extMetaData) => {
    const emails = []
    const template = EmailTypes.tenantProjects.inspectionFollowUp
    const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
    const tabIndex = getTabIndexByName('projectControl')
    const projectLinkToTenantProjectContr = getLinkToTab(tenantProjectRouteNames.tenantProjectDetail, { id: projectInfo.id }, { tabIndex })

    const hkldEmails = await getUserEmailsOfSameBuilding(PermTypeMapKeys.TENANT_PROJECT_UPDATE_INSPECTION)
    const metaData = {
      key: extMetaData.id || projectInfo.id,
      projectInfo,
      projectLink,
      projectLinkForTenant,
      projectLinkToTenantProjectContr,
      bcc: new Set(hkldEmails),
    }

    const representativeEmails = new Set(getRepresentativeEmails())
    const mainContractorEmail = getMainContractorEmail()
    const toEmails = new Set(representativeEmails)
    toEmails.add(mainContractorEmail)

    buildEmail(emails, Array.from(toEmails), template, metaData, SEND_EMAIL)
    return emails
  },

  depositNotifyPM: async (extMetaData) => {
    const emails = []
    const template = EmailTypes.tenantProjects.depositNotifyPM
    const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
    const tabIndex = getTabIndexByName('projectControl')
    const projectLinkToProjContr = getLinkToTab(tenantProjectRouteNames.tenantProject, { id: projectInfo.id }, { tabIndex })

    const canNotifyAccoutingUsers = await getUsersOfSameBuilding(PermTypeMapKeys.TENANT_PROJECT_NOTIFY_ACCOUNTING, { needTrRep: true })
    const toEmails = new Set(canNotifyAccoutingUsers.map(user => user.email || user.username))
    const deposit = store.getters['tenantProject/deposit']
    const amount = CurrencyFormatter.format(deposit.amount, 'USD')
    const metaData = {
      key: deposit.id || projectInfo.id,
      projectInfo,
      projectLink,
      projectLinkForTenant,
      projectLinkToProjContr,
      canNotifyAccoutingUsers,
      deposit: { amount },
    }
    let sendTo = ''
    toEmails.forEach(email => { sendTo = `${email};${sendTo}` })

    buildEmail(emails, sendTo, template, metaData, SEND_EMAIL)
    return emails
  },
  depositNotifyAccounting: async (extMetaData) => {
    const emails = []
    const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
    const deposit = {
      ...store.getters['tenantProject/deposit'],
      ...extMetaData.deposit,
    }
    const amount = CurrencyFormatter.format(deposit.amount, 'USD')
    const project = store.getters['tenantProject/currentTenantProject']
    const company = lodash.get(project, 'tenancy.company') || {}
    const currentRegion = store.getters['regions/currentRegion'] || {}
    const currency = currentRegion.currency

    const isCTDTypeCompany = [companyType.C, companyType.T, companyType.D].includes(company.type)
    const companyAddresses = [
      company.address1,
      company.address2,
      company.address3,
      company.address4,
    ].map(address => (address || '').trim()).filter(address => address)

    const useWording = isCTDTypeCompany || !companyAddresses.length
    const mailingAddress = useWording ? [i18n.t('tenantProjects.TenantAddress')] : companyAddresses

    const sendTo = deposit.isDirect ? mailingAddress : [`${deposit.payeeName} ${i18n.t('at')} ${deposit.address} ${i18n.t('tenantProjects.byInternalMail')}`]
    const tabIndex = getTabIndexByName('projectControl')
    const projectLinkToProjContr = getLinkToTab(tenantProjectRouteNames.tenantProject, { id: projectInfo.id }, { tabIndex })
    const ccEmails = await getUserEmailsOfSameBuilding(PermTypeMapKeys.TENANT_PROJECT_UPDATE_DEPOSIT, { needTrRep: true })
    const cc = new Set(ccEmails)

    const metaData = {
      cc,
      currency,
      key: deposit.id,
      projectInfo,
      projectLink,
      projectLinkForTenant,
      projectLinkToProjContr,
      deposit: { amount, sendTo },
      docIds: extMetaData.docIds,
      difference: extMetaData.difference,
      previous: extMetaData.previous,
      total: extMetaData.total,
    }

    const template = EmailTypes.tenantProjects.depositNotifyAccounting
    buildEmail(emails, extMetaData.email, template, metaData, SEND_EMAIL)
    return emails
  },
  managementFeeNotifyPM: async (extMetaData) => {
    const [commonMetaData, sendTo] = await buildNotifyPMMeta(extMetaData)

    const management = store.getters['tenantProject/management']
    const amount = CurrencyFormatter.format(management.amount, 'USD')
    const metaData = {
      ...commonMetaData,
      key: management.id || commonMetaData.id,
      management: { amount },
    }
    const emails = []
    const template = EmailTypes.tenantProjects.managementFeeNotifyPM
    buildEmail(emails, sendTo, template, metaData, SEND_EMAIL)
    return emails
  },
  managementFeeNotifyAccounting: (extMetaData) => {
    const commonMetaData = buildNotifyMetaData(extMetaData)
    const management = store.getters['tenantProject/management']
    const amount = CurrencyFormatter.format(management.amount, 'USD')
    const metaData = {
      ...commonMetaData,
      key: management.id || commonMetaData.id,
      management: { amount },
    }
    const emails = []
    const template = EmailTypes.tenantProjects.managementFeeNotifyAccounting
    buildEmail(emails, extMetaData.email, template, metaData, SEND_EMAIL)
    return emails
  },
  refundNotifyPM: async (extMetaData) => {
    const emails = []
    const template = EmailTypes.tenantProjects.refundNotifyPM
    const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
    const tabIndex = getTabIndexByName('projectControl')
    const projectLinkToProjContr = getLinkToTab(tenantProjectRouteNames.tenantProject, { id: projectInfo.id }, { tabIndex })

    const canNotifyAccoutingUsers = await getUsersOfSameBuilding(PermTypeMapKeys.TENANT_PROJECT_NOTIFY_ACCOUNTING, { needTrRep: true })
    const toEmails = new Set(canNotifyAccoutingUsers.map(user => user.email || user.username))
    const refund = store.getters['tenantProject/refund']
    const amount = CurrencyFormatter.format(refund.amount, 'USD')
    const metaData = {
      key: refund.id || projectInfo.id,
      projectInfo,
      projectLink,
      projectLinkForTenant,
      projectLinkToProjContr,
      canNotifyAccoutingUsers,
      refund: { amount },
    }

    let sendTo = ''
    toEmails.forEach(email => { sendTo = `${email};${sendTo}` })

    buildEmail(emails, sendTo, template, metaData, SEND_EMAIL)
    return emails
  },
  refundNotifyAccounting: async (extMetaData) => {
    const emails = []
    const template = EmailTypes.tenantProjects.refundNotifyAccounting
    const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
    const tabIndex = getTabIndexByName('projectControl')
    const projectLinkToProjContr = getLinkToTab(tenantProjectRouteNames.tenantProject, { id: projectInfo.id }, { tabIndex })
    const deposit = extMetaData.deposit || store.getters['tenantProject/deposit']
    const refund = {
      ...store.getters['tenantProject/refund'],
      ...extMetaData.refund,
    }
    const amount = CurrencyFormatter.format(refund.amount, 'USD')
    const deductedAmount = CurrencyFormatter.format((getLatestNotifyAccountingAmount(deposit) - refund.amount), 'USD')
    const currentRegion = store.getters['regions/currentRegion'] || {}
    const currency = currentRegion.currency

    const project = store.getters['tenantProject/currentTenantProject']
    const company = lodash.get(project, 'tenancy.company') || {}
    const mailingAddress = refund.isDirect ? `${company.address1}\n${company.address2}\n${company.address3}\n${company.address4}` : refund.address
    const isMailingAddressEmpty = refund.isDirect ? (!company.address1 && !company.address2 && !company.address3 && !company.address4) : false
    const ccEmails = await getUserEmailsOfSameBuilding(PermTypeMapKeys.TENANT_PROJECT_UPDATE_DEPOSIT)
    const cc = new Set(ccEmails)

    const metaData = {
      cc,
      currency,
      key: refund.id,
      projectInfo,
      projectLink,
      projectLinkForTenant,
      projectLinkToProjContr,
      refund: { amount, deductedAmount, creditTo: refund.creditTo, isDirect: refund.isDirect, payeeName: refund.payeeName, mailingAddress, isMailingAddressEmpty },
    }
    buildEmail(emails, extMetaData.email, template, metaData, SEND_EMAIL)
    return emails
  },
  requestRefund: async (extMetaData) => {
    const emails = []
    const template = EmailTypes.tenantProjects.requestRefund
    const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
    const tabIndex = getTabIndexByName('projectControl')
    const projectLinkToProjContr = getLinkToTab(tenantProjectRouteNames.tenantProject, { id: projectInfo.id }, { tabIndex })

    const refund = store.getters['tenantProject/refund']
    const toEmails = await getUserEmailsOfSameBuilding(PermTypeMapKeys.TENANT_PROJECT_REQUEST_REFUND)
    const cc = getInvitedTenancyAccountUsername(store.getters['ccd/accounts'])
    const metaData = {
      cc,
      key: refund.id,
      projectInfo,
      projectLink,
      projectLinkForTenant,
      projectLinkToProjContr,
    }
    buildEmail(emails, toEmails, template, metaData, SEND_EMAIL)
    return emails
  },
  withdrawTenantProject: async (extMetaData) => {
    const emails = []
    const template = EmailTypes.tenantProjects.withdrawTenantProject
    const [projectInfo, projectLink, projectLinkForTenant] = getProjectInfo()
    const bccEmails = await getUserEmailsOfSameBuilding(projectInfo.originType === TENANT_PROJECT_TYPES.Fitout ? PermTypeMapKeys.TENANT_PROJECT_RELATED_UPDATE : PermTypeMapKeys.TENANT_PROJECT_RELATED_UPDATE_NO_DESIGN)
    const bcc = new Set(bccEmails)
    const canSendLetterUsers = await getUsersOfSameBuilding(PermTypeMapKeys.TENANT_PROJECT_SEND)
    canSendLetterUsers.map(user => { user.email = user.email || user.username })
    const metaData = {
      projectInfo,
      projectLink,
      projectLinkForTenant,
      canSendLetterUsers,
      bcc,
      comments: extMetaData.comments,
    }
    const toEmails = new Set(getRepresentativeEmails())
    const mainContractorEmail = getMainContractorEmail()
    toEmails.add(mainContractorEmail)

    buildEmail(emails, Array.from(toEmails), template, metaData, SEND_EMAIL)
    return emails
  },
  invitePrimaryContact: async (extMetaData) => {
    const emails = []
    const tenantProject = store.getters['tenantProject/currentTenantProject']
    const passwordExpiresIn = await getEnvParameter('token.passwordExpiresIn')
    const fixData = {
      tenantProjectId: tenantProject.id,
      needResetPwdLink: true,
      fullBuildingName: lodash.get(tenantProject, 'tenancy.building.fullName'),
      premises: lodash.get(tenantProject, 'projectLocation'),
      type: i18n.t(`tenancyProject.${lodash.toLower(tenantProject.type)}`),
      originType: tenantProject.type,
      tenantCompanyName: lodash.get(tenantProject, 'tenancy.company.name'),
      linkToAccess: getPublicLink(tenantProjectRouteNames.tenantProjectDetail, tenantProject.id),
      subsidiary: lodash.get(tenantProject, 'tenancy.building.site.subsidiary'),
      passwordExpiresIn: dateUtil.transTimeUnitByLang(passwordExpiresIn),
    }

    buildEmail(emails, extMetaData.to, EmailTypes.tenantProjects.invitePrimaryContact, {...extMetaData, ...fixData}, SEND_EMAIL)
    return emails
  },
  completeFinalDocuments: async () => {
    const emails = []
    const template = EmailTypes.tenantProjects.completeFinalDocuments
    const { metaData, toEmails } = await buildFinalDocsMeta()
    buildEmail(emails, Array.from(toEmails), template, metaData, SEND_EMAIL)
    return emails
  },
  exemptFinalDocuments: async () => {
    const emails = []
    const template = EmailTypes.tenantProjects.exemptFinalDocuments
    const { metaData, toEmails } = await buildFinalDocsMeta()
    buildEmail(emails, Array.from(toEmails), template, metaData, SEND_EMAIL)
    return emails
  },
}

export default tenantProjectsNotification
